import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';

export const TSContext = createContext(null);
export const useTS = () => useContext(TSContext);

export const ROLE_LABELS = {
  owner: 'Owner',
  administrator: 'Administrator',
  supervisor: 'Supervisor',
  sales_employee: 'Sales Employee',
};

export const VOICE_LABELS = {
  selected: 'Selected',
  purchasing: 'Purchasing',
  purchased: 'Setting up',
  voice_ready: 'Voice ready',
  unavailable: 'Unavailable — pick another',
  failed: 'Needs support',
};

export const SMS_LABELS = {
  registration_required: 'Registration required',
  pool_assignment_pending: 'Pool assignment pending',
  approval_pending: 'Approval pending',
  active: 'Active',
  failed: 'Failed',
  toll_free_verification_required: 'Toll-free verification needed',
};

export const DISPOSITIONS = [
  ['connected', 'Connected'], ['no_answer', 'No answer'], ['voicemail', 'Voicemail'], ['busy', 'Busy'],
  ['qualified', 'Qualified'], ['appointment_set', 'Appointment set'], ['proposal_sent', 'Proposal sent'],
  ['won', 'Won'], ['not_interested', 'Not interested'], ['wrong_number', 'Wrong number'],
  ['call_back_later', 'Call back later'], ['do_not_call', 'Do not call'],
];

export function money(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

export function when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function useLoad(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fn());
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { data, error, loading, reload: load, setData };
}

export function Page({ title, subtitle, actions, children }) {
  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="ts-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Section({ title, children, actions, className = '' }) {
  return (
    <Card className={`bg-card border-border ${className}`}>
      <CardContent className="p-4 sm:p-5 space-y-3">
        {(title || actions) && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            {title && <h2 className="text-sm font-semibold text-foreground">{title}</h2>}
            {actions}
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

export function Stat({ label, value, hint }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold text-foreground mt-1">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

const TONES = {
  green: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  red: 'bg-red-500/15 text-red-600 dark:text-red-400',
  gray: 'bg-muted text-muted-foreground',
  blue: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  violet: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
};

export function Pill({ tone = 'gray', children, ...rest }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TONES[tone]}`} {...rest}>{children}</span>;
}

export function VoicePill({ status }) {
  const tone = status === 'voice_ready' ? 'green' : ['unavailable', 'failed'].includes(status) ? 'red' : 'amber';
  return <Pill tone={tone}>{VOICE_LABELS[status] || status}</Pill>;
}

export function SmsPill({ status }) {
  const tone = status === 'active' ? 'green' : status === 'failed' ? 'red' : 'amber';
  return <Pill tone={tone}>SMS: {SMS_LABELS[status] || status || SMS_LABELS.registration_required}</Pill>;
}

export function Empty({ children }) {
  return <p className="text-sm text-muted-foreground py-6 text-center">{children}</p>;
}

export function ErrorNote({ error }) {
  if (!error) return null;
  return <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">{error}</div>;
}

export function Loading() {
  return (
    <div className="flex justify-center py-10">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-violet-500 border-t-transparent" />
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export function Select({ value, onChange, options, className = '', ...rest }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}
      className={`h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground ${className}`} {...rest}>
      {options.map(o => Array.isArray(o)
        ? <option key={o[0]} value={o[0]}>{o[1]}</option>
        : <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export function Table({ head, children }) {
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            {head.map(h => <th key={h} className="py-2 px-2 font-medium whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">{children}</tbody>
      </table>
    </div>
  );
}

export const Td = ({ children, className = '' }) => <td className={`py-2 px-2 align-top ${className}`}>{children}</td>;
